import CancelIcon from "@mui/icons-material/Cancel";
import { Checkbox, Grid, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    NUMERIC_RANGE_FIELD_TYPE,
    SELECT_FIELD_TYPE,
    TEXT_FIELD_TYPE,
    AUTOCOMPLETE_FIELD_TYPE,
} from "../../../helpers/constants";
import usePaginationWithLocalSearchParamsAndOrdering from "../../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import { ProductStatusText } from "../../../page/ProductsPage/ProductStatusText";
import {
    useAdjacentProducts,
    useApartments,
    useInvestments,
    useProductStatuses,
    useProductTypes,
    useProductsByIds,
} from "../../../services/productsService";
import { getCalculatedPricingForProduct, useLocalizedProperty } from "../../../utils";
import { PriceDisplay } from "../../common/PriceDisplay";
import { FilterTableWrapper } from "../../table/BetterFilterTable/BetterFilterTable";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import { documentsApi } from "../../../services/documentsService";
import { isEmptyValue, isEmptyArray } from "../../../helpers/methods";

export const ProductSelectionFormControl = (props) => {
    const {
        multipleApartments,
        selectedProductIds,
        setSelectedProductIds,
        readOnly = false,
        disableApartmentSelection = false,
        excludedStatuses = [],
    } = props;
    const { t } = useTranslation();

    const [selectedInvestmentId, setSelectedInvestmentId] = useState("");
    const [checkedAllApartments, setCheckedAllApartments] = useState(false);

    const [apartmentsDataLocal, setApartmentsDataLocal] = useState([]);
    const [adjacentProductsDataLocal, setAdjacentProductsDataLocal] = useState([]);

    const { data: selectedProducts, isLoading: isSelectedProductsLoading } =
        useProductsByIds(selectedProductIds);

    const apartmentsPagination = usePaginationWithLocalSearchParamsAndOrdering();
    const adjacentProductsPagination = usePaginationWithLocalSearchParamsAndOrdering();

    const apartmentsSearchParams = useMemo(() => {
        const params = new URLSearchParams(apartmentsPagination.searchParams);

        const userSetInvestmentFilter = params.get("investment");

        if (userSetInvestmentFilter) {
            if (isEmptyValue(selectedInvestmentId) || isEmptyValue(selectedProductIds) || isEmptyArray(selectedProductIds)) {
                setSelectedInvestmentId(userSetInvestmentFilter)
            }
            return params;
        }
        if (selectedInvestmentId) {
            params.set("investment", selectedInvestmentId);
        }

        return params;
    }, [apartmentsPagination.searchParams, selectedInvestmentId]);

    const adjacentProductsSearchParams = useMemo(() => {
        const params = new URLSearchParams(adjacentProductsPagination.searchParams);

        const userSetInvestmentFilter = params.get("investment");
        if (userSetInvestmentFilter) {
            return params;
        }
        if (selectedInvestmentId) {
            params.set("investment", selectedInvestmentId);
        }

        return params;
    }, [adjacentProductsPagination.searchParams, selectedInvestmentId]);

    const { data: allApartments, isLoading: isApartmentsLoading } = useApartments(
        apartmentsSearchParams,
        {},
        excludedStatuses,
    );
    const { data: availableAdjacentProducts, isLoading: isAdjacentProductsLoading } =
        useAdjacentProducts(adjacentProductsSearchParams, {}, excludedStatuses);

    useEffect(() => {
        if (allApartments?.results) {
            setApartmentsDataLocal(allApartments.results);
        }
    }, [allApartments]);

    useEffect(() => {
        if (availableAdjacentProducts?.results) {
            setAdjacentProductsDataLocal(availableAdjacentProducts.results);
        }
    }, [availableAdjacentProducts]);

    useEffect(() => {
        const userApartmentFilter = apartmentsPagination.searchParams.get("investment");
        const userAdjacentFilter = adjacentProductsPagination.searchParams.get("investment");

        if (userApartmentFilter || userAdjacentFilter) {
            return;
        }

        if (!selectedProducts || selectedProducts.length === 0) {
            setSelectedInvestmentId("");
            return;
        }

        const firstProduct = selectedProducts[0];
        const investmentId = firstProduct?.investment?.id;

        if (investmentId) {
            setSelectedInvestmentId(investmentId);
        } else {
            setSelectedInvestmentId("");
        }
    }, [
        selectedProducts,
        apartmentsPagination.searchParams,
        adjacentProductsPagination.searchParams,
    ]);

    useEffect(() => {
        const userApartmentFilter = apartmentsPagination.searchParams.get("investment");
        const userAdjacentFilter = adjacentProductsPagination.searchParams.get("investment");

        if (
            !userApartmentFilter &&
            !userAdjacentFilter &&
            selectedInvestmentId &&
            selectedProducts?.length === 0
        ) {
            setSelectedInvestmentId("");
        }
    }, [
        apartmentsPagination.searchParams,
        adjacentProductsPagination.searchParams,
        selectedInvestmentId,
        selectedProducts,
    ]);

    const selectedApartmentsFromProducts = useMemo(() => {
        if (!selectedProducts) {
            return [];
        }
        return selectedProducts.filter((product) => product.is_apartment);
    }, [selectedProducts]);

    const selectedAdjacentProductsFromProducts = useMemo(() => {
        if (!selectedProducts) {
            return [];
        }

        const apartments = selectedApartmentsFromProducts;
        const adjacentFromApartments = [];

        // Get adjacent products from selected apartments
        apartments.forEach((apartment) => {
            apartment.children?.forEach((child) => {
                const childProduct = selectedProducts.find((p) => p.id === child.id);
                if (childProduct) {
                    adjacentFromApartments.push(childProduct);
                }
            });
        });

        // Get selected orphan adjacent products
        const selectedOrphans = selectedProducts.filter(
            (product) => !product.is_apartment && !product.parent_product?.id,
        );

        return [...adjacentFromApartments, ...selectedOrphans];
    }, [selectedProducts, selectedApartmentsFromProducts]);

    const tableFieldConfig = useFieldsConfig({});
    const tableFieldConfigWithoutCards = useFieldsConfig({excludedFields: ["card_pdf"]});
    const shortTableFieldConfig = useShortFieldsConfig();

    const toggleSelectApartment = useCallback(
        (apartmentId) => {
            const isSelected = selectedProductIds.includes(apartmentId);

            if (isSelected) {
                const apartment =
                    apartmentsDataLocal.find((p) => p.id === apartmentId) ||
                    selectedProducts?.find((p) => p.id === apartmentId);
                const childrenIds = apartment?.children?.map((p) => p.id) || [];
                const toggledProductIds = [apartmentId, ...childrenIds];

                const newSelectedProductIds = selectedProductIds.filter(
                    (id) => !toggledProductIds.includes(id),
                );
                setSelectedProductIds(newSelectedProductIds);
            } else {
                const thisApartment = apartmentsDataLocal.find((p) => p.id === apartmentId);
                if (!thisApartment) {
                    return;
                }

                const childrenIds = thisApartment.children?.map((p) => p.id) || [];
                const toggledProductIds = [thisApartment.id, ...childrenIds];

                const newSelectedProductIds = [...selectedProductIds, ...toggledProductIds];
                setSelectedProductIds(newSelectedProductIds);
            }
        },
        [selectedProductIds, apartmentsDataLocal, selectedProducts, setSelectedProductIds],
    );

    const canToggleSelectAllApartment = useMemo(
        ()=> !readOnly &&
            multipleApartments &&
            apartmentsSearchParams && 
            apartmentsSearchParams.get("investment") &&
            (isEmptyValue(selectedInvestmentId) || apartmentsSearchParams.get("investment") === selectedInvestmentId)
    , [apartmentsSearchParams, selectedInvestmentId])
    
    const toggleSelectAllApartment = useCallback(
        (checked) => {
            let productIds = apartmentsDataLocal.map(p=>p.id)
            let childrenIds = apartmentsDataLocal.flatMap(p=>p.children ? p.children.map(c=>c.id): [])
            let productWithChildrenIds = [...productIds, ...childrenIds]

            if (checked) {
                setSelectedProductIds([...selectedProductIds, ...productWithChildrenIds.filter(id=>!selectedProductIds.includes(id))])
            } else {
                setSelectedProductIds(selectedProductIds.filter(id=>!productWithChildrenIds.includes(id)))
            }
            setCheckedAllApartments(checked)
        },
        [selectedProductIds, apartmentsDataLocal, selectedProducts, setSelectedProductIds, setCheckedAllApartments],
    );

    const canToggleApartment = useCallback(
        (apartment) => {
            if (readOnly) {
                return false;
            }

            const userInvestmentFilter = apartmentsPagination.searchParams.get("investment");
            const effectiveInvestmentId = selectedInvestmentId || userInvestmentFilter;

            if (
                selectedProductIds.length > 0 &&
                effectiveInvestmentId &&
                apartment.investment.id !== effectiveInvestmentId
            ) {
                return false;
            }

            if (!multipleApartments) {
                const isCurrentlySelected = selectedProductIds.includes(apartment.id);
                const hasApartmentSelected = selectedApartmentsFromProducts.length > 0;

                if (hasApartmentSelected && !isCurrentlySelected) {
                    return false;
                }
            }

            return true;
        },
        [
            readOnly,
            selectedProductIds,
            selectedInvestmentId,
            multipleApartments,
            selectedApartmentsFromProducts,
            apartmentsPagination.searchParams,
        ],
    );

    const toggleSelectAdjacentProduct = useCallback(
        (productId) => {
            const isSelected = selectedProductIds.includes(productId);

            if (isSelected) {
                const newSelectedProductIds = selectedProductIds.filter((id) => id !== productId);
                setSelectedProductIds(newSelectedProductIds);
            } else {
                const product = adjacentProductsDataLocal.find((p) => p.id === productId);
                if (!product) {
                    return;
                }

                const newSelectedProductIds = [...selectedProductIds, productId];
                setSelectedProductIds(newSelectedProductIds);
            }
        },
        [selectedProductIds, adjacentProductsDataLocal, setSelectedProductIds],
    );

    const canToggleAdjacentProduct = useCallback(
        (product) => {
            if (readOnly) {
                return false;
            }

            const userInvestmentFilter = adjacentProductsPagination.searchParams.get("investment");
            const effectiveInvestmentId = selectedInvestmentId || userInvestmentFilter;

            if (selectedProductIds.length === 0) {
                return true;
            }

            return !effectiveInvestmentId || product.investment.id === effectiveInvestmentId;
        },
        [
            readOnly,
            selectedProductIds,
            selectedInvestmentId,
            adjacentProductsPagination.searchParams,
        ],
    );

    const canDeleteAdjacentProduct = (product) => {
        const parentId = product.parent_product?.id;
        if (!parentId) {
            return true; // Orphans can always be deleted
        }

        const parent = selectedProducts?.find((p) => p.id === parentId);
        if (parent?.is_apartment) {
            // Apartment must be selected with all its children so the children cannot be deleted on their own
            // Children of an apartment are deleted along with the apartment
            return false;
        }

        return true; // This should be unreachable according to business logic
    };

    const allApartmentsTable = () => {
        return (
            <div>
                <LoaderWrapper showLoader={isApartmentsLoading && !apartmentsDataLocal.length}>
                    <FilterTableWrapper
                        tableStyle={{maxWidth: "100%", overflowInline: "auto"}}
                        data={apartmentsDataLocal}
                        countRecords={allApartments?.count || 0}
                        localPagination={apartmentsPagination}
                        filtersOpts={{ showCleanFilterIcon: true }}
                        onSearchFiltersChange={apartmentsPagination.handleChangeSearchParams}
                        fieldsConfig={[
                            {  getHeaderLabelCallback: () => {
                                    return (
                                        multipleApartments && !readOnly ? <Checkbox
                                            onChange={(e)=>toggleSelectAllApartment(e.target.checked)}
                                            checked={checkedAllApartments}
                                            disabled={!canToggleSelectAllApartment}
                                            style={{ color: canToggleSelectAllApartment ? "var(--white)" : null}}
                                        /> : null
                                    );
                                },
                                getValueCallback: (row) => {
                                    return (
                                        <Checkbox
                                            onChange={() => toggleSelectApartment(row.id)}
                                            checked={selectedProductIds.includes(row.id)}
                                            disabled={!canToggleApartment(row)}
                                        />
                                    );
                                },
                            },
                            // @ts-ignore
                            ...tableFieldConfig,
                        ]}
                    />
                </LoaderWrapper>
            </div>
        );
    };

    const allAdjacentProductsTable = () => {
        return (
            <div>
                <LoaderWrapper
                    showLoader={isAdjacentProductsLoading && !adjacentProductsDataLocal.length}
                >
                    <FilterTableWrapper
                        tableStyle={{overflowInline: "auto"}}
                        data={adjacentProductsDataLocal}
                        countRecords={availableAdjacentProducts?.count || 0}
                        localPagination={adjacentProductsPagination}
                        filtersOpts={{ showCleanFilterIcon: true }}
                        onSearchFiltersChange={adjacentProductsPagination.handleChangeSearchParams}
                        fieldsConfig={[
                            {
                                getValueCallback: (row) => {
                                    return (
                                        <Checkbox
                                            onChange={() => toggleSelectAdjacentProduct(row.id)}
                                            checked={selectedProductIds.includes(row.id)}
                                            disabled={!canToggleAdjacentProduct(row)}
                                        />
                                    );
                                },
                            },
                            // @ts-ignore
                            ...tableFieldConfigWithoutCards,
                        ]}
                    />
                </LoaderWrapper>
            </div>
        );
    };

    const selectedApartmentsTable = () => {
        return (
            <LoaderWrapper
                showLoader={isSelectedProductsLoading && !selectedApartmentsFromProducts.length}
            >
                <FilterTableWrapper
                    tableStyle={{overflowInline: "auto"}}
                    filtersOpts={{ hideFiltersIconCol: true }}
                    data={selectedApartmentsFromProducts}
                    fieldsConfig={[
                        {
                            getValueCallback: (row) => (
                                <IconButton
                                    onClick={() => toggleSelectApartment(row.id)}
                                    disabled={readOnly || disableApartmentSelection}
                                >
                                    <CancelIcon color={"error"} />
                                </IconButton>
                            ),
                        },
                        // @ts-ignore
                        ...shortTableFieldConfig,
                    ]}
                />
            </LoaderWrapper>
        );
    };

    const selectedAdjacentProductsTable = () => {
        return (
            <LoaderWrapper
                showLoader={
                    isSelectedProductsLoading && !selectedAdjacentProductsFromProducts.length
                }
            >
                <FilterTableWrapper
                    filtersOpts={{ hideFiltersIconCol: true }}
                    data={selectedAdjacentProductsFromProducts}
                    tableStyle={{overflowInline: "auto"}}
                    fieldsConfig={[
                        {
                            getValueCallback: (row) =>
                                canDeleteAdjacentProduct(row) ? (
                                    <IconButton
                                        onClick={() => toggleSelectAdjacentProduct(row.id)}
                                        disabled={readOnly}
                                    >
                                        <CancelIcon color={"error"} />
                                    </IconButton>
                                ) : null,
                        },
                        // @ts-ignore
                        ...shortTableFieldConfig,
                    ]}
                />
            </LoaderWrapper>
        );
    };

    return (
        <Grid container direction="column" spacing={4} sx={{ p: 2 }}>
            <Grid item container direction="column" spacing={3}>
                <Grid item>
                    <Typography variant="h6" component="h3">
                        {t("ProductSelectionFormControls.allApartments")}
                    </Typography>
                </Grid>
                <Grid item sx={{ mb: 3 }}>
                    {allApartmentsTable()}
                </Grid>
                <Grid item>
                    <Typography variant="h6" component="h3">
                        {t("ProductSelectionFormControls.allAdjacentProducts")}
                    </Typography>
                </Grid>
                <Grid item sx={{ mb: 4 }}>
                    {allAdjacentProductsTable()}
                </Grid>
            </Grid>
            <Grid item container direction="row" spacing={3}>
                <Grid item xs={6} container direction="column">
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            {t("ProductSelectionFormControls.selectedApartments")}
                        </Typography>
                    </Grid>
                    <Grid item>{selectedApartmentsTable()}</Grid>
                </Grid>

                <Grid item xs={6} container direction="column">
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            {t("ProductSelectionFormControls.selectedAdjacentProducts")}
                        </Typography>
                    </Grid>
                    <Grid item>{selectedAdjacentProductsTable()}</Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

const useFieldsConfig = ({excludedFields=[]}) => {
    const { t } = useTranslation();

    const getLocalizedProperty = useLocalizedProperty();

    const { data: types } = useProductTypes(new URLSearchParams(), {
        select: (data) =>
            data.map((type) => ({
                id: type.id,
                label: getLocalizedProperty(type, "name"),
            })),
    });

    const { data: statuses } = useProductStatuses(new URLSearchParams(), {
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: t(`products.status.${status.status_name_eng}`),
            })),
    });

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    const axiosPrivate = useAxiosPrivate();
    const onPreviewCardPdf = async (e, docId) => {
            e.stopPropagation()
            try {
                await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, docId);
            } catch (error) {
                toast.error(t("snackbar_alert.occurred_error_during_preview"));
            }
        };

    const tabConfig = [
        {
            name: "product_number",
            getValueCallback: (rowData) => (
                <Grid
                    item
                    sx={{
                        minHeight: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {rowData.product_number}
                </Grid>
            ),
            label: t("products.columns.number"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },        
        {
            name: "card_pdf",
            getValueCallback: (rowData) => (
                rowData.card_pdf?.id 
                ? <IconButton onClick={(e)=>onPreviewCardPdf(e, rowData.card_pdf.id)}>
                    <DocumentScannerIcon color={"primary"} /> 
                  </IconButton>
                : null),
            label: t("products.columns.card_pdf"),
            filterType: null,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            sortable: false,
            excludedFromExport: true,
        },
        {
            name: "name_full",
            getValueCallback: (rowData) => rowData.name_full,
            label: t("products.columns.nameFull"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "type_id",
            other_export_field_name: "type",
            getValueCallback: (rowData) =>
                rowData.type ? getLocalizedProperty(rowData.type, "name") : "",
            label: t("products.columns.productType"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            minWidth: 120,
            width: 120,
            fieldConfig: {
                optionLabelKey: "label",
                options: types || [],
                multiple: true,
                isObjectOption: true,
                disableClearable: false
            },
        },
        {
            name: "investment",
            getValueCallback: (rowData) => rowData.investment.investment_name_sales,
            label: t("products.columns.investmentName"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: investments,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: investments,
            },
            sortable: false,
            minWidth: 120,
            width: 120,
        },
        {
            name: "floor",
            getValueCallback: (rowData) => rowData.floor,
            label: t("products.columns.floor"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "footage",
            getValueCallback: (rowData) => (rowData.footage ? `${rowData.footage} m²` : "-"),
            label: t("products.columns.footage"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
            minWidth: 120,
            width: 120,
            sortable: true
        },
        {
            name: "status",
            getValueCallback: (rowData) => <ProductStatusText status={rowData.status} />,
            label: t("products.columns.productStatus"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: statuses,
            }),
            sortable: false,
            minWidth: 120,
            width: 120,
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: statuses,
            },
        },
        {
            name: "listed_price_gross",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.listed.gross} />;
            },
            label: t("products.columns.priceGross"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            sortable: true
        },
        {
            name: "listed_price_per_sqm_gross",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.listed.gross_per_sqm} perSquareMeter />;
            },
            label: t("products.columns.priceGrossM2"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            sortable: true
        },
        {
            name: "listed_price_net",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.listed.net} />;
            },
            label: t("products.columns.priceNet"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortanble: true,
            minWidth: 120,
            width: 120,
            sortable: true,
        },
        {
            name: "listed_price_per_sqm_net",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.listed.net_per_sqm} perSquareMeter />;
            },
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            label: t("products.columns.priceNetM2"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            sortable: true,
        },
    ];

    return tabConfig.filter(config=>!excludedFields.includes(config.name))
};

const useShortFieldsConfig = () => {
    const { t } = useTranslation();

    return [
        {
            name: "product_number",
            getValueCallback: (rowData) => (
                <Grid
                    item
                    sx={{
                        minHeight: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {rowData.product_number}
                </Grid>
            ),
            label: t("products.columns.number"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
            minWidth: 120,
            width: 120,
        },
        {
            name: "name_full",
            getValueCallback: (rowData) => rowData.name_full,
            label: t("products.columns.nameFull"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
            minWidth: 120,
            width: 120,
        },
    ];
};
